import { Event, SDK, ServerOptions } from '@commercetools/frontend-sdk';
import {
  AddCartItemPayload,
  CheckoutPayload,
  GetCartShippingMethodsPayload,
  GetTaxRatePayload,
  GetDeliveryOptionsPayload,
  GetStoreOpeningTimePayload,
  RedeemDiscountCodePayload,
  RemoveCartItemPayload,
  RemoveDiscountCodePayload,
  SetCartShippingMethodPayload,
  SetDeliveryOptionPayload,
  UpdateCartItemPayload,
  UpdateCartPayload,
  GetEstimatedShippingPricePayload,
  RemoveGiftcardPayload,
  SetDeliveryInstructionPayload,
  AddOrderIdToCartPayload,
  FreezeCartActionPayload,
  UnFreezeCartActionPayload,
} from '../../types/payloads/CartPayloads';
import {
  AddCartItemAction,
  CheckoutAction,
  GetAvailableCartShippingMethodsAction,
  GetCartAction,
  GetCartShippingMethodsAction,
  GetDeliveryOptionsAction,
  QueryOrdersAction,
  RedeemDiscountCodeAction,
  RemoveCartItemAction,
  RemoveDiscountCodeAction,
  SetCartShippingMethodAction,
  UpdateCartAction,
  UpdateCartItemAction,
  GetTaxRateAction,
  GetEstimatedShippingPriceAction,
  GetStoresAction,
  SetDeliveryOptionAction,
  GetStoreOpeningTimeAction,
  RemoveGiftcardAction,
  SetDeliveryInstruction,
  CartRecalculateAction,
  AddOrderIdToCart,
  FreezeCart,
  UnFreezeCart,
} from '../../types/actions/CartActions';
import { Cart, ShippingMethod, Order, TaxRateData, Store, DeliveryOption, StoreOpeningTime } from 'shared/types/cart';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import { QueryOrdersQuery } from '../../types/queries/CartQueries';
import { PaginatedResult } from 'shared/types/result';
import { Money } from 'shared/types/product';
import { cookieHandler } from 'sdk/CommercetoolsSDK';

export type CartActions = {
  getCart: GetCartAction;
  addItem: AddCartItemAction;
  checkout: CheckoutAction;
  addOrderIdToCart: AddOrderIdToCart;
  removeItem: RemoveCartItemAction;
  updateItem: UpdateCartItemAction;
  updateCart: UpdateCartAction;
  getShippingMethods: GetCartShippingMethodsAction;
  getAvailableShippingMethods: GetAvailableCartShippingMethodsAction;
  getAvailableDeliveryOptions: GetDeliveryOptionsAction;
  setDeliveryOption: SetDeliveryOptionAction;
  setShippingMethod: SetCartShippingMethodAction;
  redeemDiscountCode: RedeemDiscountCodeAction;
  removeDiscountCode: RemoveDiscountCodeAction;
  removeGiftcard: RemoveGiftcardAction;
  queryOrders: QueryOrdersAction;
  getTaxCategoryRate: GetTaxRateAction;
  getStores: GetStoresAction;
  getEstimatedShippingPrice: GetEstimatedShippingPriceAction;
  getStoreOpeningTime: GetStoreOpeningTimeAction;
  setDeliveryInstruction: SetDeliveryInstruction;
  cartRecalculate: CartRecalculateAction;
  freezeCart: FreezeCart;
  unfreezeCart: UnFreezeCart;
};
export interface ExtendedServerOptions extends ServerOptions {
  context?: Record<string, any>;
}
export const getCartActions = (sdk: SDK<ComposableCommerceEvents>): CartActions => {
  return {
    getCart: async (options: { serverOptions?: ExtendedServerOptions } = {}) => {
      const guestSession = await cookieHandler.getCookie('guest-session');
      const payload = guestSession ? guestSession : undefined;
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/getCart',
        payload,
        serverOptions: options.serverOptions,
      });
      //Cart type does not declare sessionData, so cast to any when checking it
      const guestSessionObj = guestSession ? JSON.parse(guestSession as string) : null;
      const reseponseSessionData = !response.isError ? (response.data as any)?.sessionData : null;
      if (
        reseponseSessionData &&
        (!guestSession ||
          (guestSessionObj?.checkoutToken?.token !== reseponseSessionData?.checkoutToken?.token &&
            guestSessionObj?.anonymousId &&
            guestSessionObj?.anonymousId !== reseponseSessionData?.anonymousId &&
            guestSessionObj?.cartId &&
            guestSessionObj?.cartId !== reseponseSessionData?.cartId))
      ) {
        const newguestSession = JSON.stringify(reseponseSessionData);
        await cookieHandler.setCookie('guest-session', newguestSession, {
          path: '/',
          httpOnly: false,
          sameSite: 'none',
          secure: true,
          maxAge: 60 * 60 * 24 * 90,
        });
      }
      return response;
    },
    addItem: async (payload: AddCartItemPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/addToCart',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productAddedToCart',
            data: {
              product: payload.variant,
              quantity: payload.variant.count,
              amount: payload?.variant?.amount,
              type: payload?.variant?.type,
              recipient: payload?.variant?.recipient,
              recipientemail: payload?.variant?.recipientemail,
              cid: payload?.variant?.cid,
              // customFitOptions: payload?.variant?.customFitOptions,
              customPrice: payload?.variant?.customPrice,
              ...payload,
              // pid: payload?.variant?.sku,
            },
          }),
        );
      }
      return response;
    },
    checkout: async (payload: CheckoutPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/checkout',
        payload,
        serverOptions: options.serverOptions,
      });
      const reseponseData = !response.isError ? response.data : null;
      const guestSession = await cookieHandler.getCookie('guest-session');
      if (reseponseData && guestSession) {
        await cookieHandler.setCookie('guest-session', '', { path: '/', maxAge: 0 });
      }
      return response;
    },
    addOrderIdToCart: async (payload: AddOrderIdToCartPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/addOrderIdToCart',
        payload,
        serverOptions: options.serverOptions,
      });

      return response;
    },
    removeItem: async (payload: RemoveCartItemPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/removeLineItem',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productRemovedFromCart',
            data: {
              product: payload.lineItem,
              quantity: 1,
            },
          }),
        );
      }
      return response;
    },
    updateItem: async (payload: UpdateCartItemPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/updateLineItem',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'productUpdatedInCart',
            data: {
              product: {
                id: payload.lineItem.id,
              },
              newQuantity: payload.lineItem.count,
            },
          }),
        );
      }
      return response;
    },
    updateCart: async (payload: UpdateCartPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/updateCart',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'cartUpdated',
            data: payload,
          }),
        );
      }
      return response;
    },
    getShippingMethods: async (
      payload?: GetCartShippingMethodsPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ShippingMethod[]>({
        actionName: 'cart/getShippingMethods',
        query: payload?.query ?? undefined,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'shippingMethodsFetched',
            data: {
              shippingMethods: response.data,
            },
          }),
        );
      }
      return response;
    },
    getAvailableShippingMethods: async (options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<ShippingMethod[]>({
        actionName: 'cart/getAvailableShippingMethods',
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'availableShippingMethodsFetched',
            data: {
              shippingMethods: response.data,
            },
          }),
        );
      }
      return response;
    },

    setShippingMethod: async (
      payload: SetCartShippingMethodPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/setShippingMethod',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'shippingMethodUpdated',
            data: {
              shippingMethod: response.data.shippingInfo,
            },
          }),
        );
      }
      return response;
    },
    redeemDiscountCode: async (payload: RedeemDiscountCodePayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart | string>({
        actionName: 'cart/redeemDiscount',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'discountCodeRedeemed',
            data: {
              discountCode: typeof response.data !== 'string' ? payload.code : response.data,
              cart: typeof response.data !== 'string' ? response.data : undefined,
            },
          }),
        );
      }
      return response;
    },
    removeDiscountCode: async (payload: RemoveDiscountCodePayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/removeDiscount',
        payload,
        serverOptions: options.serverOptions,
      });

      if (response.isError === false) {
        sdk.trigger(
          new Event({
            eventName: 'discountCodeRemoved',
            data: {
              discountCode: payload.discountId,
              cart: response.data,
            },
          }),
        );
      }
      return response;
    },
    removeGiftcard: async (payload: RemoveGiftcardPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/removeGiftcard',
        payload,
        serverOptions: options.serverOptions,
      });

      return response;
    },
    getAvailableDeliveryOptions: async (
      payload: GetDeliveryOptionsPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<DeliveryOption[]>({
        actionName: 'cart/getAvailableDeliveryOptions',
        payload: payload,
        serverOptions: options.serverOptions,
      });

      return response;
    },

    setDeliveryOption: async (payload: SetDeliveryOptionPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/setDeliveryOption',
        payload: payload,
        serverOptions: options.serverOptions,
      });

      return response;
    },

    getTaxCategoryRate: async (payload: GetTaxRatePayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<TaxRateData>({
        actionName: 'cart/getTaxCategoryRate',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },

    getEstimatedShippingPrice: async (
      payload: GetEstimatedShippingPricePayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<Money>({
        actionName: 'cart/getEstimatedShippingPrice',
        payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },

    cartRecalculate: async () => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/cartRecalculate',
      });
      return response;
    },

    queryOrders: async (
      query?: QueryOrdersQuery,
      options: {
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<PaginatedResult<Order>>({
        actionName: 'cart/queryOrders',
        query,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    getStores: async () => {
      const response = await sdk.callAction<Store[]>({
        actionName: 'cart/getStores',
      });
      return response;
    },
    getStoreOpeningTime: async (
      payload: GetStoreOpeningTimePayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<StoreOpeningTime>({
        actionName: 'cart/getStoreOpeningTime',
        payload: payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    setDeliveryInstruction: async (
      payload: SetDeliveryInstructionPayload,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/setDeliveryInstruction',
        payload: payload,
        serverOptions: options.serverOptions,
      });

      return response;
    },
    freezeCart: async (payload: FreezeCartActionPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/freezeCart',
        payload: payload,
        serverOptions: options.serverOptions,
      });

      return response;
    },
    unfreezeCart: async (payload: UnFreezeCartActionPayload, options: { serverOptions?: ServerOptions } = {}) => {
      const response = await sdk.callAction<Cart>({
        actionName: 'cart/unfreezeCart',
        payload: payload,
        serverOptions: options.serverOptions,
      });
      return response;
    },
  };
};
